import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import type { ConversationThread, DeckCheckRow, ReconcileInputClaim, ReconcileResult, ReportSource, ReviewSchedule, ReviewWindowRule, ReviewSection } from '../../types';
import type { Claim } from '../../components/Ladder/claims';
import type { LadderSuggestion, ReviewTemplate, ThreadRow } from '../../apis';
import { claimKey } from '../../components/Ladder/claims';
import {
  adjustClaim,
  getImportSample,
  getThread,
  patchReviewSectionGroup,
  getLiveReportSample,
  importArtifact as apiImportArtifact,
  listLiveReportSamples,
  readLiveReport,
  listReviewTemplates,
  runReconcile as apiRunReconcile,
  patchReviewSection,
  pinToReview,
  putReviewCommentary,
  putReviewWindowRule,
  refreshReview as apiRefreshReview,
  scheduleReview as apiScheduleReview,
  startReviewDoc,
  threadRowToConversation,
  unpinReviewSection as apiUnpinReviewSection,
  upsertThread, patchThread } from '../../apis';
import { generateId } from '../../utils';
import { suggestionPinKey } from './pinsSlice';

/**
 * Review documents (#189). A review is not a separate feature: it is a
 * `kind: doc` thread whose content — the template's layout, the numbers and
 * suggestions pinned from other threads, the schedule — is server-managed
 * and refreshed by the review's own workflow. This slice is the store's
 * half: the one creation screen's actions, pinning, scheduling, refreshing.
 * Threads themselves (tabs, star, close, history) stay in `threadsSlice`.
 */

export type ReviewStage = 'new' | 'draft' | 'live';

export function isReviewDoc(t: ConversationThread | null | undefined): boolean {
  return !!t && t.kind === 'doc';
}

export function reviewStage(t: ConversationThread | null | undefined): ReviewStage {
  const stage = t?.doc?.stage;
  return stage === 'draft' || stage === 'live' ? stage : 'new';
}

/** A review that can take a pin: started (draft or live) and open as a tab. */
export function isLivingReview(t: ConversationThread | null | undefined): boolean {
  return isReviewDoc(t) && !t!.closedAt && reviewStage(t) !== 'new';
}

export function reviewSections(t: ConversationThread | null | undefined): ReviewSection[] {
  return (t?.doc?.sections || []) as ReviewSection[];
}

/**
 * The pinned claim as it stands today: the refreshed value over the
 * refreshed window, so a Why asked from the review walks the days the
 * number now shows — and the popover knows the number is already here.
 * #408 writes the window back into the claim; this fallback still serves older rows.
 */
export function refreshedClaim(section: ReviewSection): Claim {
  const claim = (section.claim || {}) as Claim;
  const g = claim.grounding;
  const window = section.window && g
    ? { ...(g.window || {}), start: section.window.start ?? g.window?.start, end: section.window.end ?? g.window?.end }
    : g?.window;
  return { ...claim, value: section.value ?? claim.value, grounding: g ? { ...g, window } : g };
}

/** A pinned number's move since the last refresh, as the review prints it: "▲ 18.3%" — null when there is no prior to compare with. */
export function deltaText(delta: number | null | undefined, previous: any): string | null {
  const prev = typeof previous === 'number' ? previous : Number(previous);
  if (delta == null || !Number.isFinite(prev) || prev === 0) return null;
  const p = (delta / Math.abs(prev)) * 100;
  // only an exact zero is no change (callers say so); a real move too small to show keeps its direction
  if (delta === 0) return null;
  return `${p >= 0 ? '▲' : '▼'} ${Math.abs(p) < 0.05 ? '<0.1' : Math.abs(p).toFixed(1)}%`;
}

/** How far a flagged number moved against its prior period — the move the walk judged, not the change since the last refresh. */
export function moveText(s: ReviewSection): string | null {
  const pct = s.why?.significance?.delta_pct;
  if (typeof pct === 'number' && Number.isFinite(pct) && Math.abs(pct) >= 0.05) return `${pct >= 0 ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%`;
  return deltaText(s.delta, s.previous);
}

/** How a section is named on the review: the number's label with its row, or the suggestion's text. */
export function sectionTitle(s: ReviewSection): string {
  if (s.kind === 'suggestion') return s.suggestion?.text || 'suggestion';
  const c = s.claim || {};
  const row = c.grounding?.row || {};
  const rowText = Object.keys(row).length ? ' · ' + Object.entries(row).map(([k, v]) => `${k}=${v}`).join(', ') : '';
  return `${c.label || c.grounding?.metric || 'number'}${rowText}`;
}

/** Every living review that would take a number in this perspective — the choice a person makes when more than one could. */
export function pinCandidates(threads: ConversationThread[], perspective: string | null | undefined): ConversationThread[] {
  return threads.filter((t) => reviewTakes(t, perspective))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

/** The analyses opened from a review section — every thread stamped with it — newest first. Closed ones count: the digging is not lost with the tab. */
export function analysesOfSection(threads: ConversationThread[], sectionId: string | null | undefined): ConversationThread[] {
  if (!sectionId) return [];
  return threads.filter((t) => t.kind !== 'doc' && t.sourceKind === 'review_section' && t.sourceId === sectionId)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

/** How many turns a thread holds — what "3 turns" on the review means. */
export function turnCount(t: ConversationThread): number {
  return (t.landingAgentTurns || []).length;
}

/** The review a section belongs to, and the section itself — for a thread opened from one. */
export function reviewOfSection(threads: ConversationThread[], sectionId: string | null | undefined): { review: ConversationThread; section: ReviewSection } | null {
  if (!sectionId) return null;
  for (const t of threads) {
    if (!isReviewDoc(t)) continue;
    const section = reviewSections(t).find((s) => s.id === sectionId);
    if (section) return { review: t, section };
  }
  return null;
}

/** Whether the review already holds this number — as pinned, or as it stands after a refresh. */
export function reviewHasKey(t: ConversationThread | null | undefined, key: string): boolean {
  return reviewSections(t).some((s) => s.kind !== 'template'
    && (s.key === key || s.origin_key === key || s.key_history?.some((h) => h.key === key) || (s.kind === 'claim' && s.claim?.grounding && claimKey(refreshedClaim(s)) === key)));
}

/** The perspectives a review is grounded on: the set (#190) when it has one, else the one it was started on. */
export function reviewGrounding(t: ConversationThread | null | undefined): string[] {
  const many = (t?.doc?.perspectives || []).filter(Boolean);
  if (many.length) return many;
  const one = t?.doc?.perspective_id || t?.perspectiveId || null;
  return one ? [one] : [];
}

/** A review can take a number only from its own grounding — fixed at build; a set when it was built from an artifact. */
export function reviewTakes(t: ConversationThread | null | undefined, perspective: string | null | undefined): boolean {
  if (!isLivingReview(t) || !perspective) return false;
  const mine = reviewGrounding(t);
  return mine.length === 0 || mine.includes(perspective);
}

/** The living review most recently changed, whatever its grounding — for a label, never for a pin. */
export function latestLivingReview(threads: ConversationThread[]): ConversationThread | null {
  return threads.filter(isLivingReview)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0] || null;
}

/**
 * Where a pin lands (the ticket's rule): the review on screen; else the
 * living review most recently changed; else nowhere — the Home queue holds
 * it until a review is built. Only a review grounded on the number's own
 * perspective qualifies (the server refuses any other), and the button that
 * pins says which, so nothing lands somewhere unexpected.
 */
export function pinDestination(threads: ConversationThread[], currentThreadId: string | null,
                               perspective: string | null | undefined): ConversationThread | null {
  const current = threads.find((t) => t.id === currentThreadId);
  if (current && reviewTakes(current, perspective)) return current;
  const living = threads.filter((t) => reviewTakes(t, perspective))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  return living[0] || null;
}

export function describeSchedule(schedule: ReviewSchedule | null | undefined): string {
  if (!schedule) return 'not scheduled';
  if (schedule.follow === 'source') return 'when the report refreshes';
  const day = (schedule.weekday || '').slice(0, 3);
  return `${day.charAt(0).toUpperCase()}${day.slice(1)} ${schedule.at} UTC`;
}

export function describeWindowRule(rule: ReviewWindowRule | null | undefined): string {
  if (!rule) return 'no window rule — each number rolls its own length, ending the last complete day (later periods stay put)';
  if (rule.kind === 'last_complete_week') return 'last complete week (Mon–Sun)';
  if (rule.kind === 'trailing_days') return `trailing ${rule.days} days, ending the last complete day`;
  return "frozen on the source artifact's period";
}

/** #408: a Mon–Sun artifact implies a weekly review; other historical periods stay put by default. */
export function artifactWindowRule(window: ReconcileInput['window']): ReviewWindowRule {
  if (window) {
    const start = new Date(`${window.start}T00:00:00Z`);
    const end = new Date(`${window.end}T00:00:00Z`);
    if (start.getUTCDay() === 1 && end.getTime() - start.getTime() === 6 * 86400000) return { kind: 'last_complete_week' };
  }
  return { kind: 'frozen' };
}

/** Exact-key lineage only: ranked notes still ask about the number's current window (#408). */
export function sectionClaimKeys(section: ReviewSection | null | undefined, currentKey: string): string[] {
  return Array.from(new Set([currentKey, ...(section?.key ? [section.key] : []), ...(section?.origin_key ? [section.origin_key] : []), ...(section?.key_history || []).map((h) => h.key)]));
}

export function priorKeyWindow(section: ReviewSection | null | undefined, currentKey: string, key?: string | null): string | null {
  if (!key || key === currentKey || !sectionClaimKeys(section, currentKey).includes(key)) return null;
  const window = section?.key_history?.find((h) => h.key === key)?.window
    || (section?.origin_key === key ? section.source_window : null)
    || (section?.key === key ? section.claim?.grounding?.window : null);
  return window?.start && window?.end ? `${window.start} → ${window.end}` : 'an earlier window';
}

export type ReviewBusy = 'creating' | 'starting' | 'pinning' | 'scheduling' | 'refreshing' | 'saving' | 'reconciling' | 'building';

export interface ReconcileInput {
  /** The claims graded so far (every page). */
  claims: ReconcileInputClaim[];
  window: { start: string; end: string } | null;
  perspective: string | null;
  artifact: { name?: string; kind?: string; file?: string; week?: string | null; id?: string | null; claims_found?: number; truncated?: boolean;
    provider?: string; refreshed_at?: string | null; as_of?: string | null; view?: string } | null;
  /** Deck import (#191): numbers the artifact printed that no page has graded yet — kept, never dropped; "Grade the next page" takes them. */
  pending?: ReconcileInputClaim[];
  /** What one reconcile request takes (the server's cap). */
  pageSize?: number;
  /** The checked-in demo deck, not the reader's own. */
  sample?: boolean;
  /** A live report (#192): the review built from this grading follows it. */
  source?: ReportSource | null;
}

export const RECONCILE_PAGE = 120;
/** A review holds so many pinned sections (the server's MAX_SECTIONS). */
export const REVIEW_MAX_SECTIONS = 200;

/**
 * The first page of a deck: the tables go first — a scorecard is what a
 * reader trusts — then the prose; the page is graded in deck order, so the
 * rows read the way the deck does, and everything else waits as `pending`.
 */
export function claimsToGrade(claims: ReconcileInputClaim[], max: number): { claims: ReconcileInputClaim[]; pending: ReconcileInputClaim[] } {
  const indexed = claims.map((c, i) => ({ c, i }));
  const tables = indexed.filter(({ c }) => c.confidence !== 'prose');
  const prose = indexed.filter(({ c }) => c.confidence === 'prose');
  const ordered = [...tables, ...prose];
  const n = Math.max(1, max);
  // The page reads in deck order; what waits keeps its priority, so the next page still takes tables before prose.
  return { claims: ordered.slice(0, n).sort((a, b) => a.i - b.i).map(({ c }) => c), pending: ordered.slice(n).map(({ c }) => c) };
}

/** Two gradings as one: the rows in order, the scoreboard summed. */
export function mergeReconcile(a: ReconcileResult | null, b: ReconcileResult): ReconcileResult {
  const ids = (x: ReconcileResult | null) => (x ? (x.session_ids && x.session_ids.length ? x.session_ids : (x.session_id ? [x.session_id] : [])) : []);
  if (!a) return b;
  const scoreboard = { ...a.scoreboard } as Record<string, number>;
  for (const [k, v] of Object.entries(b.scoreboard)) scoreboard[k] = (scoreboard[k] || 0) + (typeof v === 'number' ? v : 0);
  // every page's session is kept: the first is the grading's, the rest are listed with it (an untraced grading adds nothing)
  const session_ids = [...ids(a), ...ids(b)];
  return { ...b, scoreboard: scoreboard as unknown as ReconcileResult['scoreboard'], claims: [...a.claims, ...b.claims],
    ...(session_ids.length ? { session_id: session_ids[0], session_ids } : {}) };
}

export interface ReconcileState {
  input: ReconcileInput;
  result: ReconcileResult | null;
  error: string | null;
  /** Draft definitions opted into for this grading (a term defined on the needs-you row). */
  useDefinitions: string[];
}

export interface ReviewSource {
  thread_id?: string | null;
  thread_title?: string | null;
  session_id?: string | null;
  /** Where in the artifact the number was read from ('slide 3 · table row 2 col 1'), #191. */
  ref?: string | null;
}

export interface ReviewsSlice {
  reviewsBusy: Record<string, ReviewBusy | undefined>;
  reviewsError: string | null;
  reviewTemplates: ReviewTemplate[];
  /** The live reports the deployment can show without a connection (#192). */
  liveReportSamples: Array<{ id: string; name: string; available: boolean }>;
  /**
   * The source a NEW thread should record (what it was opened from): set
   * right before a walk is asked from a review section, read once by the
   * persist that creates the thread.
   */
  pendingThreadSource: { kind: string; id: string } | null;

  /**
   * Reconcile (#190), per unstarted review tab: the artifact being graded and
   * the scoreboard. Lives here so the tab keeps its result while you look
   * elsewhere and comes back to it.
   */
  reconcile: Record<string, ReconcileState | undefined>;

  fetchReviewTemplates: () => Promise<void>;
  /**
   * "+ New review": the unstarted review if there is one, else a fresh doc tab. Unstarted reviews are deduped.
   * Resolves with the review's id once the server has it (the setup dialog starts it then), null if it never took it.
   * `open: false` leaves the tab where it is — the setup opens it once the start went through, so a failed start
   * keeps its dialog and its choices.
   */
  newReview: (opts?: { open?: boolean }) => Promise<string | null>;
  /** `deckRows`: the start is a build from this tab's grading (#568) — the ids of the rows it graded, which the review's deck check is exactly. */
  startReview: (threadId: string, templateId: string, perspectiveId: string, perspectives?: string[], source?: ReportSource | null, windowRule?: ReviewWindowRule, deckRows?: string[]) => Promise<boolean>;
  fetchLiveReportSamples: () => Promise<void>;
  /** Connect a live report (#192): read its scorecard and grade every tile, grounded where the reader chose (the sample brings its own). */
  connectReport: (threadId: string, report: { connectionId?: string | null; sampleId?: string | null }, perspectiveId?: string | null) => Promise<boolean>;
  /** Grade a checked-in sample artifact (demo data) in this review tab — through the same importer an upload goes through. */
  reconcileSample: (threadId: string, sampleId: string) => Promise<boolean>;
  /** Deck import (#191): read the reader's own artifact and grade every number it printed, grounded where they chose. */
  importArtifact: (threadId: string, file: File, perspectiveId?: string | null) => Promise<boolean>;
  /** Grade the next page of a deck's numbers (the ones still pending) and add them to the grading on screen. */
  reconcileNextPage: (threadId: string) => Promise<boolean>;
  /** Grade claims (again — e.g. with a draft definition opted in) in this review tab. */
  reconcileClaims: (threadId: string, input: ReconcileInput, useDefinitions?: string[]) => Promise<boolean>;
  clearReconcile: (threadId: string) => void;
  /** "Build this week's review": start the WBR grounded on every perspective the artifact resolved to, pin every number that reproduced. */
  buildReviewFromReconcile: (threadId: string, windowRule?: ReviewWindowRule) => Promise<boolean>;
  pinClaimToReview: (threadId: string, claim: Claim, source: ReviewSource) => Promise<ReviewSection | null>;
  pinSuggestionToReview: (threadId: string, suggestion: LadderSuggestion, source: ReviewSource) => Promise<ReviewSection | null>;
  unpinReviewSection: (threadId: string, sectionId: string) => Promise<void>;
  /** Resolves true when the commentary is on the server; false when it is not (the error is in `reviewsError`). */
  setReviewSectionCommentary: (threadId: string, sectionId: string, text: string) => Promise<boolean>;
  /**
   * Add an analysis to a review of the person's choosing: its number becomes a
   * tile there, and the thread is stamped as an analysis of that tile, so the
   * review lists it and resumes it. Resolves the new section, or null.
   */
  attachThreadToReview: (reviewId: string, claim: Claim, threadId: string) => Promise<ReviewSection | null>;
  setReviewCommentary: (threadId: string, text: string) => Promise<void>;
  /** Move a tile under another of the template's groups (#568); null = the first. */
  setReviewSectionGroup: (threadId: string, sectionId: string, group: string | null) => Promise<boolean>;
  /**
   * "Use <X>'s definition for this deck" (#568): the section is re-grounded on
   * the definition the deck's value picked and a signed source mapping is
   * saved with it (one transaction, #196); the review is then read back.
   */
  mapDeckDefinition: (threadId: string, sectionId: string, row: DeckCheckRow) => Promise<boolean>;
  setReviewWindowRule: (threadId: string, rule: ReviewWindowRule | null) => Promise<boolean>;
  scheduleReview: (threadId: string, schedule: ReviewSchedule | null) => Promise<boolean>;
  refreshReview: (threadId: string, asOf?: string | null) => Promise<boolean>;
  /** Every queued pin goes into the review — the first one built takes the queue. */
  flushPinsInto: (threadId: string) => Promise<void>;
}

function errorText(err: unknown, fallback: string): string {
  return (err as any)?.response?.data?.error || (err as any)?.message || fallback;
}

/** Reviews whose server row is still being written, by id: a second "+ New review" waits for the first's row instead of starting a review the server has not got. */
const creating = new Map<string, Promise<unknown>>();

export const createReviewsSlice: StateCreator<VoyagerState, [], [], ReviewsSlice> = (set, get) => {
  const busy = (threadId: string, what: ReviewBusy | undefined) =>
    set({ reviewsBusy: { ...get().reviewsBusy, [threadId]: what } } as Partial<VoyagerState>);
  /**
   * One generation per tab, monotonic for the life of the store — it
   * survives a clear, so a grading cleared and started again cannot be
   * answered by the earlier request's late response.
   */
  const generations = new Map<string, number>();
  const nextGeneration = (threadId: string) => {
    const g = (generations.get(threadId) || 0) + 1;
    generations.set(threadId, g);
    return g;
  };

  /**
   * Take the server's row for the thread — the doc is the server's to keep.
   * Two mutations that overlap are serialised on the server, but their
   * responses can land out of order: a row whose doc is OLDER than the one
   * already here (the server bumps `doc.version` on every change) is a late
   * answer to an earlier change and is not taken, or it would briefly undo
   * the later one.
   */
  const takeRow = (row: ThreadRow | null | undefined) => {
    if (!row) return;
    const { workflow: _w, execution: _e, ...plain } = row as any;
    const fromServer = threadRowToConversation(plain);
    const existing = get().savedThreads;
    const current = existing.find((t) => t.id === fromServer.id);
    const have = Number(current?.doc?.version ?? -1);
    const got = Number(fromServer.doc?.version ?? -1);
    if (current && have > got) return;
    const savedThreads = current
      ? existing.map((t) => (t.id === fromServer.id ? { ...t, ...fromServer } : t))
      : [fromServer, ...existing];
    set({ savedThreads } as Partial<VoyagerState>);
  };

  const titleOf = (threadId: string | null | undefined) =>
    (threadId && get().savedThreads.find((t) => t.id === threadId)?.title) || null;

  const pinnedCount = (thread: ConversationThread | undefined): number =>
    ((thread?.doc?.sections || []) as ReviewSection[]).filter((s) => s.kind === 'claim' || s.kind === 'suggestion').length;

  /** The server takes so many claims per request: more are graded in pages, one after another, and merged. */
  const gradeInPages = async (input: ReconcileInput, claims: ReconcileInputClaim[], defs: string[], threadId?: string | null): Promise<ReconcileResult> => {
    const size = Math.max(1, input.pageSize || RECONCILE_PAGE);
    let merged: ReconcileResult | null = null;
    for (let at = 0; at < claims.length; at += size) {
      const page = await apiRunReconcile({ thread_id: threadId || null, claims: claims.slice(at, at + size), window: input.window, perspective: input.perspective,
        artifact: input.artifact ? { name: input.artifact.name, kind: input.artifact.kind, file: input.artifact.file, week: input.artifact.week, id: input.artifact.id } : input.artifact,
        ...(defs.length ? { use_definitions: defs } : {}) });
      merged = mergeReconcile(merged, page);
    }
    return merged as ReconcileResult;
  };

  const pin = async (threadId: string, body: Parameters<typeof pinToReview>[1]): Promise<ReviewSection | null> => {
    busy(threadId, 'pinning');
    try {
      const result = await pinToReview(threadId, body);
      takeRow(result.thread);
      set({ reviewsError: null } as Partial<VoyagerState>);
      return result.section;
    } catch (err) {
      set({ reviewsError: errorText(err, 'Could not pin to the review') } as Partial<VoyagerState>);
      return null;
    } finally {
      busy(threadId, undefined);
    }
  };

  return {
    reviewsBusy: {},
    reviewsError: null,
    reviewTemplates: [],
    pendingThreadSource: null,
    reconcile: {},
    liveReportSamples: [],

    fetchReviewTemplates: async () => {
      try {
        const templates = await listReviewTemplates();
        set({ reviewTemplates: templates } as Partial<VoyagerState>);
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not load the review templates') } as Partial<VoyagerState>);
      }
    },

    newReview: async (opts) => {
      const open = opts?.open !== false;
      const threads = get().savedThreads;
      // One unstarted review at a time: finish or close it before another.
      // A closed one stays closed — closing the creation tab is how you
      // walk away from it; it waits in History, and this makes a new one.
      const unstarted = threads.find((t) => isReviewDoc(t) && !t.closedAt && reviewStage(t) === 'new');
      if (unstarted) {
        if (open) get().loadThread(unstarted.id);
        // still being written: its id is no use until the server has the row
        const pending = creating.get(unstarted.id);
        if (pending) {
          try { await pending; } catch { return null; }
        }
        return unstarted.id;
      }
      const id = generateId();
      const now = new Date().toISOString();
      const selected = get().selectedPerspectiveId;
      const perspectiveId = selected && selected !== 'auto' ? selected : null;
      const thread: ConversationThread = {
        id, title: 'New review', preview: '', messages: [], createdAt: now, updatedAt: now, spaceId: null,
        detectedPerspective: perspectiveId, detectedDomain: null, landingAgentTurns: [], perspectiveId, domainId: null,
        kind: 'doc', starred: false, closedAt: null, lastOpenedAt: now, tabOrder: 0, sourceKind: null, sourceId: null, doc: {},
      };
      set({ savedThreads: [thread, ...threads] } as Partial<VoyagerState>);
      // The tab is the creation screen: the doc exists on the server from
      // the moment it is a tab, so the dedupe holds across a reload. Until
      // the server has it the screen offers no template — a start that
      // reached the server first would find no thread to start.
      busy(id, 'creating');
      const created = upsertThread(id, { kind: 'doc', title: 'New review', perspective_id: perspectiveId });
      creating.set(id, created);
      created.then(() => creating.delete(id), () => creating.delete(id));
      if (open) get().loadThread(id);
      try {
        takeRow(await created);
        busy(id, undefined);
        return id;
      } catch (err) {
        // The server never took it: the tab goes, or every template pick
        // would 404 and "+ New review" would keep reopening a phantom.
        busy(id, undefined);
        set({
          savedThreads: get().savedThreads.filter((t) => t.id !== id),
          ...(get().currentThreadId === id ? { currentThreadId: null } : {}),
          reviewsError: errorText(err, 'Could not create the review — try again'),
        } as Partial<VoyagerState>);
        return null;
      }
    },

    fetchLiveReportSamples: async () => {
      try {
        set({ liveReportSamples: await listLiveReportSamples() } as Partial<VoyagerState>);
      } catch {
        set({ liveReportSamples: [] } as Partial<VoyagerState>);
      }
    },

    connectReport: async (threadId, report, perspectiveId) => {
      busy(threadId, 'reconciling');
      set({ reviewsError: null } as Partial<VoyagerState>);
      try {
        const read = report.sampleId ? await getLiveReportSample(report.sampleId) : await readLiveReport(report.connectionId as string);
        if (!read.claims.length) {
          busy(threadId, undefined);
          set({ reviewsError: `No numbers on ${read.artifact.name}'s scorecard sheet — check the connection's sheet name` } as Partial<VoyagerState>);
          return false;
        }
        const pageSize = read.max_claims || RECONCILE_PAGE;
        const { claims, pending } = claimsToGrade(read.claims, pageSize);
        const source: ReportSource = { ...(read.source as ReportSource), name: read.artifact.name, refreshed_at: read.artifact.refreshed_at || null };
        const input: ReconcileInput = { claims, pending, pageSize, sample: !!report.sampleId, source, window: read.window,
          perspective: perspectiveId || read.perspective || null, artifact: { ...read.artifact, kind: 'report' } };
        set({ reconcile: { ...get().reconcile, [threadId]: { input, result: null, error: null, useDefinitions: [] } } } as Partial<VoyagerState>);
      } catch (err) {
        busy(threadId, undefined);
        set({ reviewsError: errorText(err, 'Could not read the report') } as Partial<VoyagerState>);
        return false;
      }
      busy(threadId, undefined);
      return get().reconcileClaims(threadId, get().reconcile[threadId]!.input);
    },

    startReview: async (threadId, templateId, perspectiveId, perspectives, source, windowRule, deckRows) => {
      busy(threadId, 'starting');
      try {
        takeRow(await startReviewDoc(threadId, { template_id: templateId, perspective_id: perspectiveId,
          ...(perspectives && perspectives.length ? { perspectives } : {}), ...(source ? { source } : {}), ...(windowRule ? { window_rule: windowRule } : {}), ...(deckRows && deckRows.length ? { keep_deck_check: { rows: deckRows } } : {}) }));
        set({ reviewsError: null } as Partial<VoyagerState>);
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not start the review') } as Partial<VoyagerState>);
        busy(threadId, undefined);
        return false;
      }
      busy(threadId, undefined);
      // Numbers pinned before any review existed were waiting for this one.
      if (get().pins.length) await get().flushPinsInto(threadId);
      return true;
    },

    reconcileSample: async (threadId, sampleId) => {
      busy(threadId, 'reconciling');
      try {
        const sample = await getImportSample(sampleId);
        const pageSize = sample.max_claims || RECONCILE_PAGE;
        const { claims, pending } = claimsToGrade(sample.claims, pageSize);
        const input: ReconcileInput = { claims, pending, pageSize, sample: true, window: sample.window, perspective: sample.perspective,
          artifact: { ...sample.artifact, kind: sample.artifact.kind || 'deck' } };
        set({ reconcile: { ...get().reconcile, [threadId]: { input, result: null, error: null, useDefinitions: [] } } } as Partial<VoyagerState>);
      } catch (err) {
        busy(threadId, undefined);
        set({ reviewsError: errorText(err, 'Could not load the sample') } as Partial<VoyagerState>);
        return false;
      }
      busy(threadId, undefined);
      return get().reconcileClaims(threadId, get().reconcile[threadId]!.input);
    },

    importArtifact: async (threadId, file, perspectiveId) => {
      busy(threadId, 'reconciling');
      set({ reviewsError: null } as Partial<VoyagerState>);
      try {
        const read = await apiImportArtifact(file);
        if (!read.claims.length) {
          busy(threadId, undefined);
          set({ reviewsError: `No numbers found in ${file.name} — the reader looks for tables and figures in the text` } as Partial<VoyagerState>);
          return false;
        }
        const pageSize = read.max_claims || RECONCILE_PAGE;
        const { claims, pending } = claimsToGrade(read.claims, pageSize);
        // The window is the deck's own when it names one; the grounding is the
        // reader's choice on the creation screen, never guessed from the file.
        const input: ReconcileInput = { claims, pending, pageSize, window: read.window, perspective: perspectiveId || read.perspective || null,
          artifact: { ...read.artifact, kind: read.artifact.kind || 'deck' } };
        set({ reconcile: { ...get().reconcile, [threadId]: { input, result: null, error: null, useDefinitions: [] } } } as Partial<VoyagerState>);
      } catch (err) {
        busy(threadId, undefined);
        set({ reviewsError: errorText(err, `Could not read ${file.name}`) } as Partial<VoyagerState>);
        return false;
      }
      busy(threadId, undefined);
      return get().reconcileClaims(threadId, get().reconcile[threadId]!.input);
    },

    reconcileClaims: async (threadId, input, useDefinitions) => {
      const prior = get().reconcile[threadId];
      const defs = useDefinitions ?? prior?.useDefinitions ?? [];
      const generation = nextGeneration(threadId);
      busy(threadId, 'reconciling');
      set({ reconcile: { ...get().reconcile, [threadId]: { input, result: prior?.result ?? null, error: null, useDefinitions: defs } } } as Partial<VoyagerState>);
      // Only the newest request's answer lands: a slower, older grading (an
      // earlier definition set, or one cleared meanwhile) must not replace it.
      const current = () => generations.get(threadId) === generation && !!get().reconcile[threadId];
      try {
        const result = await gradeInPages(input, input.claims, defs, threadId);
        if (!current()) return false;
        set({ reconcile: { ...get().reconcile, [threadId]: { input, result, error: null, useDefinitions: defs } } } as Partial<VoyagerState>);
        return true;
      } catch (err) {
        if (!current()) return false;
        set({ reconcile: { ...get().reconcile, [threadId]: { input, result: prior?.result ?? null, error: errorText(err, 'Could not reconcile'), useDefinitions: defs } } } as Partial<VoyagerState>);
        return false;
      } finally {
        if (current()) busy(threadId, undefined);
      }
    },

    reconcileNextPage: async (threadId) => {
      const state = get().reconcile[threadId];
      if (!state || !state.result || !state.input.pending?.length || get().reviewsBusy[threadId]) return false;
      const size = state.input.pageSize || RECONCILE_PAGE;
      const page = state.input.pending.slice(0, size);
      const rest = state.input.pending.slice(size);
      const generation = nextGeneration(threadId);
      busy(threadId, 'reconciling');
      const current = () => generations.get(threadId) === generation && !!get().reconcile[threadId];
      try {
        const graded = await gradeInPages(state.input, page, state.useDefinitions, threadId);
        if (!current()) return false;
        const input: ReconcileInput = { ...state.input, claims: [...state.input.claims, ...page], pending: rest };
        set({ reconcile: { ...get().reconcile, [threadId]: { ...state, input, result: mergeReconcile(state.result, graded), error: null } } } as Partial<VoyagerState>);
        return true;
      } catch (err) {
        if (!current()) return false;
        // The page stays pending: nothing is lost, the button asks again.
        set({ reconcile: { ...get().reconcile, [threadId]: { ...state, error: errorText(err, 'Could not grade the next page') } } } as Partial<VoyagerState>);
        return false;
      } finally {
        if (current()) busy(threadId, undefined);
      }
    },

    clearReconcile: (threadId) => {
      // Leaving invalidates a grading still in flight (its answer will find
      // no state to land in) — and releases the tab, or the creation screen
      // would stay disabled until a reload.
      const { [threadId]: _gone, ...rest } = get().reconcile;
      set({ reconcile: rest } as Partial<VoyagerState>);
      nextGeneration(threadId);
      if (get().reviewsBusy[threadId] === 'reconciling') busy(threadId, undefined);
    },

    buildReviewFromReconcile: async (threadId, windowRule) => {
      // Pages still pending are graded first: the review's grounding is
      // fixed when it starts (#189), so it must be the whole artifact's set,
      // not the first page's.
      while (get().reconcile[threadId]?.input.pending?.length) {
        if (!(await get().reconcileNextPage(threadId))) return false;
      }
      const state = get().reconcile[threadId];
      const result = state?.result;
      if (!result) return false;
      // Every number that reproduced is a claim in its own perspective; the
      // review is grounded on all of them (#190), the artifact's own first.
      const pinnable = result.claims.filter((c) => c.claim?.grounding?.perspective && ['match', 'ambiguous', 'stale'].includes(c.verdict));
      const perspectives = Array.from(new Set([state.input.perspective, ...pinnable.map((c) => c.claim.grounding.perspective as string)].filter(Boolean))) as string[];
      const primary = state.input.perspective || perspectives[0];
      if (!pinnable.length || !primary) {
        // Nothing reproduced: an empty review would only throw the grading
        // away — and the grading is what resolves the failures.
        set({ reviewsError: 'Nothing reproduced yet — define the unknown terms or fix the mismatches before building a review' } as Partial<VoyagerState>);
        return false;
      }
      busy(threadId, 'building');
      try {
        // Idempotent: a build that failed part-way left the review started
        // and some numbers pinned; the retry starts nothing twice and pins
        // only what is not there yet.
        const thread = get().savedThreads.find((t) => t.id === threadId);
        if (reviewStage(thread) === 'new') {
          const ok = await get().startReview(threadId, 'wbr', primary, perspectives, state.input.source || null, windowRule || artifactWindowRule(state.input.window),
            result.claims.map((c) => c.id));
          if (!ok) return false;
          // A review built from a live report follows it (#192): refreshed when the report is.
          if (state.input.source && !(await get().scheduleReview(threadId, { follow: 'source' }))) return false;
        }
        const title = state.input.artifact?.name || 'the artifact';
        const failed: string[] = [];
        const unplaced: string[] = [];
        for (const c of pinnable) {
          const claim = { ...c.claim, label: c.label } as Claim;
          const current = get().savedThreads.find((t) => t.id === threadId);
          if (reviewHasKey(current, claimKey(claim))) continue;
          // A review holds so many numbers: the ones beyond its capacity are
          // named here, never sent to fail one by one on every build.
          if (pinnedCount(current) >= REVIEW_MAX_SECTIONS) { unplaced.push(c.label); continue; }
          const section = await get().pinClaimToReview(threadId, claim, { thread_id: null,
            thread_title: state.input.source ? `connected from ${title}` : `reconciled from ${title}`, ref: c.artifact.source_ref || null });
          if (!section) failed.push(c.label);
        }
        if (unplaced.length) {
          set({ reviewsError: `A review holds ${REVIEW_MAX_SECTIONS} numbers — ${unplaced.length} reproduced ${unplaced.length === 1 ? 'number was' : 'numbers were'} not pinned (${unplaced.slice(0, 3).join(', ')}${unplaced.length > 3 ? ', …' : ''}); unpin some and build again, or keep the grading as the record` } as Partial<VoyagerState>);
          return false;
        }
        if (failed.length) {
          // The grading stays on screen, so nothing reproduced is silently left out.
          set({ reviewsError: `${pinnable.length - failed.length} of ${pinnable.length} numbers pinned — could not pin ${failed.join(', ')}; build again to pin the rest` } as Partial<VoyagerState>);
          return false;
        }
        get().clearReconcile(threadId);
        return true;
      } finally {
        busy(threadId, undefined);
      }
    },

    pinClaimToReview: (threadId, claim, source) => {
      if (!claim.grounding) return Promise.resolve(null);
      return pin(threadId, {
        kind: 'claim', key: claimKey(claim), claim,
        source: { ...source, thread_title: source.thread_title ?? titleOf(source.thread_id) },
      });
    },

    pinSuggestionToReview: (threadId, suggestion, source) => {
      // A suggestion was derived in its thread's perspective; it is pinned
      // with it, and a thread whose perspective is unknown cannot pin one.
      const perspective = source.thread_id ? (get().savedThreads.find((t) => t.id === source.thread_id)?.perspectiveId || null) : null;
      if (!perspective) {
        set({ reviewsError: 'This suggestion\'s thread has no perspective on record, so it cannot be pinned to a review' } as Partial<VoyagerState>);
        return Promise.resolve(null);
      }
      return pin(threadId, {
        kind: 'suggestion', key: suggestionPinKey(suggestion, source.thread_id || null), suggestion, perspective,
        source: { ...source, thread_title: source.thread_title ?? titleOf(source.thread_id) },
      });
    },

    unpinReviewSection: async (threadId, sectionId) => {
      busy(threadId, 'saving');
      try {
        takeRow(await apiUnpinReviewSection(threadId, sectionId));
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not unpin') } as Partial<VoyagerState>);
      } finally {
        busy(threadId, undefined);
      }
    },

    attachThreadToReview: async (reviewId, claim, threadId) => {
      // The number becomes a tile (idempotent: a re-pin of the same claim returns its section),
      // then the thread is linked back to that tile. BOTH must land for the review to list this
      // analysis; a link that fails returns null so the caller keeps the retry, and a retry only
      // re-links (the pin is already there).
      const section = await get().pinClaimToReview(reviewId, claim, { thread_id: threadId });
      if (!section) return null;
      try {
        const row = await patchThread(threadId, { source_kind: 'review_section', source_id: section.id });
        set({ reviewsError: null, savedThreads: get().savedThreads.map((t) => (t.id === threadId
          ? { ...t, sourceKind: row?.source_kind ?? 'review_section', sourceId: row?.source_id ?? section.id, updatedAt: row?.updated_at ?? t.updatedAt } : t)) } as Partial<VoyagerState>);
      } catch (err) {
        // the tile is on the review, but this thread is not linked to it: not a success — the caller retries the link
        set({ reviewsError: errorText(err, 'Pinned the number, but could not link this analysis — try again') } as Partial<VoyagerState>);
        return null;
      }
      return section;
    },

    setReviewSectionCommentary: async (threadId, sectionId, text) => {
      try {
        takeRow(await patchReviewSection(threadId, sectionId, text));
        return true;
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not save the commentary') } as Partial<VoyagerState>);
        return false;
      }
    },

    setReviewCommentary: async (threadId, text) => {
      try {
        takeRow(await putReviewCommentary(threadId, text));
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not save the commentary') } as Partial<VoyagerState>);
      }
    },

    setReviewSectionGroup: async (threadId, sectionId, group) => {
      try {
        takeRow(await patchReviewSectionGroup(threadId, sectionId, group));
        set({ reviewsError: null } as Partial<VoyagerState>);
        return true;
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not move the number') } as Partial<VoyagerState>);
        return false;
      }
    },

    mapDeckDefinition: async (threadId, sectionId, row) => {
      const def = row.resolved;
      if (!def?.metric_qid || !def.perspective) {
        set({ reviewsError: `No definition to map “${row.label}” to` } as Partial<VoyagerState>);
        return false;
      }
      // The number keeps its own slice: a grouped cell (Region = West) is re-grounded under the new definition WITH
      // its filters — omitted, the adjust would read the unfiltered total and pin that under the cell's label.
      const section = reviewSections(get().savedThreads.find((t) => t.id === threadId)).find((s) => s.id === sectionId);
      const filters = (section?.claim?.grounding?.filters || row.claim?.grounding?.filters || []) as Array<{ dimension: string; operator?: string; value: any }>;
      busy(threadId, 'saving');
      try {
        await adjustClaim({ artifact: { kind: 'review', thread_id: threadId, section_id: sectionId }, claim: { id: sectionId, label: row.label },
          grounding: { metric_qid: def.metric_qid, perspective: def.perspective, filters }, remember: true });
      } catch (err) {
        set({ reviewsError: errorText(err, `Could not map “${row.label}” to ${def.perspective_name || def.metric}'s definition`) } as Partial<VoyagerState>);
        busy(threadId, undefined);
        return false;
      }
      try {
        takeRow(await getThread(threadId));
        set({ reviewsError: null } as Partial<VoyagerState>);
      } catch (err) {
        // the mapping is saved; only the page is behind — said, not hidden
        set({ reviewsError: errorText(err, 'The mapping is saved, but the review could not be read back — reopen it') } as Partial<VoyagerState>);
      } finally {
        busy(threadId, undefined);
      }
      return true;
    },

    setReviewWindowRule: async (threadId, rule) => {
      busy(threadId, 'saving');
      try {
        takeRow(await putReviewWindowRule(threadId, rule));
        set({ reviewsError: null } as Partial<VoyagerState>);
        return true;
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not save the window rule') } as Partial<VoyagerState>);
        return false;
      } finally {
        busy(threadId, undefined);
      }
    },

    scheduleReview: async (threadId, schedule) => {
      busy(threadId, 'scheduling');
      try {
        takeRow(await apiScheduleReview(threadId, schedule));
        set({ reviewsError: null } as Partial<VoyagerState>);
        return true;
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not schedule the review') } as Partial<VoyagerState>);
        return false;
      } finally {
        busy(threadId, undefined);
      }
    },

    refreshReview: async (threadId, asOf) => {
      busy(threadId, 'refreshing');
      try {
        const row = await apiRefreshReview(threadId, asOf);
        takeRow(row);
        const failed = row.execution && row.execution.status !== 'completed';
        set({ reviewsError: failed ? (row.execution?.error || 'The refresh did not complete') : null } as Partial<VoyagerState>);
        return !failed;
      } catch (err) {
        set({ reviewsError: errorText(err, 'Could not refresh the review') } as Partial<VoyagerState>);
        return false;
      } finally {
        busy(threadId, undefined);
      }
    },

    flushPinsInto: async (threadId) => {
      const review = get().savedThreads.find((t) => t.id === threadId);
      for (const p of [...get().pins]) {
        // A number (or a suggestion, derived in its thread's perspective)
        // from another perspective stays in the queue for a review of its own.
        const from = p.suggestion
          ? (get().savedThreads.find((t) => t.id === p.threadId)?.perspectiveId || null)
          : p.claim?.grounding?.perspective;
        if (!reviewTakes(review, from)) continue;
        const source = { thread_id: p.threadId, thread_title: titleOf(p.threadId) };
        const section = p.suggestion
          ? await get().pinSuggestionToReview(threadId, p.suggestion, source)
          : await get().pinClaimToReview(threadId, p.claim, source);
        if (section) get().unpin(p.id);
      }
    },
  };
};
